/**
Project: Voice Panel (c)
Title: Inventory Actions 
Description: Inventory actions for creating/modifying numbers
Copyrights: This file is subject to the terms and conditions defined in file 'LICENSE.txt', which is part of this source code package.
*/
import axios from "axios";
import { AppConfigProps } from "../../../../core/settings/app-config";
import {
  logMessage,
  checkIdentitySession,
  getSessionExpiredError,
} from "../../../../core/actions/common-actions";
import { capitalizeFirstLetter } from "../../common/actions/general-actions";
import {
  INVENTORY_NUMBERS_PAGE_RECORDS,
  INVENTORY_NUMBERS_DISPLAY_RECORDS,
  INVENTORY_NUMBERS_ALL_RECORDS,
  INVENTORY_DEVICE_MAKE_MODEL_DATA,
  COMUNICATION_PLATFORMS_DATA,
} from "../../../../store/voice-admin/constants";
import _ from "lodash";
import { VoiceAdminConstants } from "../../common/settings/voice-admin-constants";
import { includes, orderBy } from "lodash";
import IntlUtil from "../../../../core/helpers/intl-util";

const actionFileName = "number-inventory-actions.js";

export function getCountryData(cancelToken) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .get(
          AppConfigProps.serverRoutePrefix + "/general/country-states",
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">getCountryData()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      reject(getSessionExpiredError());
    }
  });
}

export function getSubscriberAddress(cancelToken) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .get(
          AppConfigProps.serverRoutePrefix + "/subscriber-address",
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">getSubscriberAddress()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      reject(getSessionExpiredError());
    }
  });
}

export function setSubscriberAddress(data, cancelToken) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .post(
          AppConfigProps.serverRoutePrefix + "/subscriber-address",
          data,
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch(async (err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">setSubscriberAddress()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      reject(getSessionExpiredError());
    }
  });
}

export function getInventoryNumbers(cancelToken) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .get(
          AppConfigProps.serverRoutePrefix + "/subscribers/numbers",
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">getInventoryNumbers()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      reject(getSessionExpiredError());
    }
  });
}

export function deleteInventoryNumber(numberId, cancelToken) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .delete(
          AppConfigProps.serverRoutePrefix + `/subscribers/numbers/${numberId}`,
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">deleteInventoryNumbers()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      reject(getSessionExpiredError());
    }
  });
}

export function setInventoryNumbersPageAllRecords(inventoryNumbersAllRecords) {
  return async function (dispatch) {
    try {
      dispatch({
        type: INVENTORY_NUMBERS_ALL_RECORDS,
        payload: inventoryNumbersAllRecords,
      });
    } catch (err) {
      throw Error(err);
    }
  };
}

export function setInventoryNumbersDisplayRecords(
  inventoryNumbersDisplayRecords
) {
  return async function (dispatch) {
    try {
      dispatch({
        type: INVENTORY_NUMBERS_DISPLAY_RECORDS,
        payload: inventoryNumbersDisplayRecords,
      });
    } catch (err) {
      throw Error(err);
    }
  };
}

export function setInventoryNumbersPageRecords(inventoryNumbersPageRecords) {
  return async function (dispatch) {
    try {
      dispatch({
        type: INVENTORY_NUMBERS_PAGE_RECORDS,
        payload: inventoryNumbersPageRecords,
      });
    } catch (err) {
      throw Error(err);
    }
  };
}

export function getDialCodeByCountry(countryList, selectedCountryName) {
  if (countryList && selectedCountryName) {
    for (const country of countryList) {
      if (
        country.countryName.trim().toLowerCase() ===
        selectedCountryName.trim().toLowerCase()
      ) {
        return country.dialCode;
      }
    }
  }
}

export function removeDialCodefromPhoneNumber(phoneNumber, numberDialCode) {
  if (phoneNumber.startsWith(numberDialCode) === true) {
    let number = phoneNumber.replace(numberDialCode, "");
    return number;
  } else {
    return phoneNumber;
  }
}

// remove Dial Code from PhoneNumber by country
export function removePhoneNumberDialCodebyCountryName(
  countryList,
  phoneNumber,
  numberCountryName
) {
  if (countryList && phoneNumber && numberCountryName) {
    for (const country of countryList) {
      if (
        country.countryName.trim().toLowerCase() ===
        numberCountryName.trim().toLowerCase()
      ) {
        if (phoneNumber.startsWith(country.dialCode) === true) {
          let number = phoneNumber.replace(country.dialCode, "");
          return number;
        } else {
          return phoneNumber;
        }
      }
    }
  }

  return phoneNumber;
}

export function orderByName(recordList, displayParams) {
  if (recordList && recordList.length > 0) {
    let sortedList = orderBy(
      recordList,
      [displayParams.orderName],
      [displayParams.ascending]
    );
    return sortedList;
  }
}

export function sortInventoryNumbers(list, sortColumn, sortOrder) {
  return new Promise(async function (resolve, reject) {
    if (list && list.length > 0) {
      let sortRecords = list.sort((firstItem, secondItem) => {
        let fItem = firstItem[sortColumn] !== null ? firstItem[sortColumn] : "";
        let sItem =
          secondItem[sortColumn] !== null ? secondItem[sortColumn] : "";
        if (sortOrder === VoiceAdminConstants.TABLE_DATA.SORT_ORDER.ASCENDING) {
          return fItem.localeCompare(sItem, "en", {
            numeric: true,
          });
        } else {
          return sItem.localeCompare(fItem, "en", {
            numeric: true,
          });
        }
        //break;
      });
      resolve(sortRecords);
    } else {
      resolve(list);
    }
  });
}

export function sliceInventoryNumbers(list, pageOffset, pageLimit) {
  return new Promise(async function (resolve, reject) {
    if (list && list.length > 0 && pageOffset >= 0 && pageLimit > 0) {
      let start = pageOffset * pageLimit;
      let end = start + pageLimit;
      if (end > list.length) end = list.length;

      let sliceRecords = await list.slice(start, end);
      resolve(sliceRecords);
    } else {
      resolve(list);
    }
  });
}

export function searchInventoryNumbers(
  list,
  searchKeyword,
  voiceNumbersPrivilege
) {
  return new Promise(async function (resolve, reject) {
    if (
      list &&
      list.length > 0 &&
      searchKeyword &&
      searchKeyword.trim().length > 0
    ) {
      let searchRecords = list.filter((inventoryNumber) => {
        if (voiceNumbersPrivilege === true) {
          return (
            includes(
              inventoryNumber?.numberCountryName?.toLowerCase(),
              searchKeyword.trim().toLowerCase()
            ) ||
            includes(
              inventoryNumber?.phoneNumber?.toLowerCase(),
              searchKeyword.trim().toLowerCase()
            ) ||
            includes(
              inventoryNumber?.assignStatus?.toLowerCase(),
              searchKeyword.trim().toLowerCase()
            ) ||
            includes(
              inventoryNumber?.userName?.toLowerCase(),
              searchKeyword.trim().toLowerCase()
            ) ||
            includes(
              inventoryNumber?.numberTypeName?.toLowerCase(),
              searchKeyword.trim().toLowerCase()
            ) ||
            includes(
              inventoryNumber?.departmentName?.toLowerCase(),
              searchKeyword.trim().toLowerCase()
            ) ||
            includes(
              inventoryNumber?.manageStatus?.toLowerCase(),
              searchKeyword.trim().toLowerCase()
            ) ||
            includes(
              inventoryNumber?.classification?.toLowerCase(),
              searchKeyword.trim().toLowerCase()
            ) ||
            includes(
              inventoryNumber?.reserveStatus?.toLowerCase(),
              searchKeyword.trim().toLowerCase()
            ) ||
            includes(
              inventoryNumber?.city?.toLowerCase(),
              searchKeyword.trim().toLowerCase()
            ) ||
            includes(
              inventoryNumber?.country?.toLowerCase(),
              searchKeyword.trim().toLowerCase()
            ) ||
            includes(
              inventoryNumber?.carrierName?.toLowerCase(),
              searchKeyword.trim().toLowerCase()
            ) ||
            includes(
              inventoryNumber?.deviceTypeName?.toLowerCase(),
              searchKeyword.trim().toLowerCase()
            ) ||
            includes(
              inventoryNumber?.deviceSerialNumber?.toLowerCase(),
              searchKeyword.trim().toLowerCase()
            )
          );
        } else {
          return (
            includes(
              inventoryNumber?.numberCountryName?.toLowerCase(),
              searchKeyword.trim().toLowerCase()
            ) ||
            includes(
              inventoryNumber?.phoneNumber?.toLowerCase(),
              searchKeyword.trim().toLowerCase()
            ) ||
            includes(
              inventoryNumber?.assignStatus?.toLowerCase(),
              searchKeyword.trim().toLowerCase()
            ) ||
            includes(
              inventoryNumber?.userName?.toLowerCase(),
              searchKeyword.trim().toLowerCase()
            ) ||
            includes(
              inventoryNumber?.numberTypeName?.toLowerCase(),
              searchKeyword.trim().toLowerCase()
            ) ||
            includes(
              inventoryNumber?.departmentName?.toLowerCase(),
              searchKeyword.trim().toLowerCase()
            ) ||
            includes(
              inventoryNumber?.manageStatus?.toLowerCase(),
              searchKeyword.trim().toLowerCase()
            ) ||
            includes(
              inventoryNumber?.classification?.toLowerCase(),
              searchKeyword.trim().toLowerCase()
            ) ||
            includes(
              inventoryNumber?.reserveStatus?.toLowerCase(),
              searchKeyword.trim().toLowerCase()
            ) ||
            includes(
              inventoryNumber?.city?.toLowerCase(),
              searchKeyword.trim().toLowerCase()
            ) ||
            includes(
              inventoryNumber?.country?.toLowerCase(),
              searchKeyword.trim().toLowerCase()
            ) ||
            includes(
              inventoryNumber?.deviceTypeName?.toLowerCase(),
              searchKeyword.trim().toLowerCase()
            ) ||
            includes(
              inventoryNumber?.deviceSerialNumber?.toLowerCase(),
              searchKeyword.trim().toLowerCase()
            )
          );
        }
      });

      resolve(searchRecords);
    } else {
      resolve(list);
    }
  });
}

export function getInventoryNumberById(numberId, cancelToken) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .get(
          AppConfigProps.serverRoutePrefix + `/subscribers/numbers/${numberId}`,
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">getInventoryNumberById()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      reject(getSessionExpiredError());
    }
  });
}

export function addNumber(numberData, cancelToken) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .post(
          AppConfigProps.serverRoutePrefix + "/subscribers/numbers",
          numberData,
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">addNumber()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      reject(getSessionExpiredError());
    }
  });
}

export function updateNumber(numberData, numberId, cancelToken) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .put(
          AppConfigProps.serverRoutePrefix + `/subscribers/numbers/${numberId}`,
          numberData,
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">updateNumber()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      reject(getSessionExpiredError());
    }
  });
}

function dataEditor(data) {
  let filteredData = data.replace(/"/g, '"');
  return `"${filteredData.replace(/\n/g, "")}"`;
}

// Get inventory Data (Export Users)
export function getInventoryFileData(intlNamespace, inventoryData) {
  let headerColumns = [];
  headerColumns.push(
    IntlUtil.getText(intlNamespace, "content.countryName").replace(
      new RegExp(" ", "g"),
      ""
    ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.phoneNumber").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.userName").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.streetAddress1").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.streetAddress2").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.city").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.state").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.country").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.postalCode").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.carrier").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.type").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.department").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.managedOrunmanaged").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.classification").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.assignedOrunassigned").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.reservedOrunreserved").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.notes").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "\n"
  );
  let dataColumns = [];
  if (inventoryData && inventoryData.length > 0) {
    dataColumns = inventoryData.map((data) => {
      let number = removeDialCodefromPhoneNumber(
        data.phoneNumber,
        data.numberDialCode
      );
      let countryName = "";
      if (
        data.numberCountryName != null &&
        data.numberCountryName.trim().length > 0
      )
        countryName = dataEditor(data.numberCountryName);
      let phoneNumber = "";
      if (number != null && number.trim().length > 0)
        phoneNumber = dataEditor(number);
      let userName = "";
      if (data.userName != null && data.userName.trim().length > 0)
        userName = dataEditor(data.userName);
      let streetAddress1 = "";
      if (data.streetAddress1 != null && data.streetAddress1.trim().length > 0)
        streetAddress1 = dataEditor(data.streetAddress1);
      let streetAddress2 = "";
      if (data.streetAddress2 != null && data.streetAddress2.trim().length > 0)
        streetAddress2 = dataEditor(data.streetAddress2);
      let city = "";
      if (data.city != null && data.city.trim().length > 0)
        city = dataEditor(data.city);
      let state = "";
      if (data.state != null && data.state.trim().length > 0)
        state = dataEditor(data.state);
      let country = "";
      if (data.country != null && data.country.trim().length > 0)
        country = dataEditor(data.country);
      let postalCode = "";
      if (data.postalCode != null && data.postalCode.trim().length > 0)
        postalCode = dataEditor(data.postalCode);
      let carrierName = "";
      if (data.carrierName != null && data.carrierName.trim().length > 0)
        carrierName = dataEditor(data.carrierName);
      let numberTypeName = "";
      if (data.numberTypeName != null && data.numberTypeName.trim().length > 0)
        numberTypeName = dataEditor(data.numberTypeName);
      let departmentName = "";
      if (data.departmentName != null && data.departmentName.trim().length > 0)
        departmentName = dataEditor(data.departmentName);
      let manageStatus = "";
      if (data.manageStatus != null && data.manageStatus.trim().length > 0)
        manageStatus = dataEditor(data.manageStatus);
      let classification = "";
      if (data.classification != null && data.classification.trim().length > 0)
        classification =
          data.classification?.toLowerCase().trim() ===
          VoiceAdminConstants.INVENTORY.CLASSIFICATION_TYPE.E_FAX?.toLowerCase().trim()
            ? VoiceAdminConstants.INVENTORY.CLASSIFICATION_TYPE.E_FAX
            : dataEditor(capitalizeFirstLetter(data.classification));
      let assignStatus = "";
      if (data.assignStatus != null && data.assignStatus.trim().length > 0)
        assignStatus = dataEditor(data.assignStatus);
      let reserveStatus = "";
      if (data.reserveStatus != null && data.reserveStatus.trim().length > 0)
        reserveStatus = dataEditor(data.reserveStatus);
      let numberNotes = "";
      if (data.numberNotes != null && data.numberNotes.trim().length > 0)
        numberNotes = dataEditor(data.numberNotes);

      return (
        countryName +
        "," +
        phoneNumber +
        "," +
        userName +
        "," +
        streetAddress1 +
        "," +
        streetAddress2 +
        "," +
        city +
        "," +
        state +
        "," +
        country +
        "," +
        postalCode +
        "," +
        carrierName +
        "," +
        numberTypeName +
        "," +
        departmentName +
        "," +
        manageStatus +
        "," +
        classification +
        "," +
        assignStatus +
        "," +
        reserveStatus +
        "," +
        numberNotes
      );
    });
  }

  return headerColumns.join("\n").concat(dataColumns.join("\n"));
}

export function getDeviceFileData(countryList, intlNamespace, deviceData) {
  let headerColumns = [];
  headerColumns.push(
    IntlUtil.getText(intlNamespace, "content.countryName").replace(
      new RegExp(" ", "g"),
      ""
    ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.phoneNumber").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.deviceType").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.makeBrand").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.model").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.serialNumber").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "\n"
  );
  let dataColumns = [];
  if (deviceData && deviceData.length > 0) {
    dataColumns = deviceData.map((data) => {
      let number = removePhoneNumberDialCodebyCountryName(
        countryList,
        data.phoneNumber,
        data.numberCountryName
      );
      let countryName = "";
      if (
        data.numberCountryName != null &&
        data.numberCountryName.trim().length > 0
      )
        countryName = dataEditor(data.numberCountryName);
      let phoneNumber = "";
      if (number != null && number.trim().length > 0)
        phoneNumber = dataEditor(number);
      let deviceTypeName = "";
      if (data.deviceTypeName != null && data.deviceTypeName.trim().length > 0)
        deviceTypeName = dataEditor(data.deviceTypeName);
      let makeName = "";
      if (data.makeName != null && data.makeName.trim().length > 0)
        makeName = dataEditor(data.makeName);
      let modelName = "";
      if (data.modelName != null && data.modelName.trim().length > 0)
        modelName = dataEditor(data.modelName);
      let serialNumber = "";
      if (data.serialNumber != null && data.serialNumber.trim().length > 0)
        serialNumber = dataEditor(data.serialNumber);

      return (
        countryName +
        "," +
        phoneNumber +
        "," +
        deviceTypeName +
        "," +
        makeName +
        "," +
        modelName +
        "," +
        serialNumber
      );
    });
  }

  return headerColumns.join("\n").concat(dataColumns.join("\n"));
}

export function getDepartmentsList(cancelToken) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .get(
          AppConfigProps.serverRoutePrefix + "/subscribers/departments",
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">getDepartmentsList()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      reject(getSessionExpiredError());
    }
  });
}
export function getNumberTypeList(cancelToken) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .get(
          AppConfigProps.serverRoutePrefix + "/subscribers/number-types",
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">getNumberTypeList()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      reject(getSessionExpiredError());
    }
  });
}
export function getCarriersList(cancelToken) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .get(
          AppConfigProps.serverRoutePrefix + "/subscribers/carriers",
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">getCarriersList()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      reject(getSessionExpiredError());
    }
  });
}

// Upload Inventory (Import Inventory)
export function uploadInventory(numbersData, cancelToken) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .post(
          AppConfigProps.serverRoutePrefix + "/subscribers/numbers/bulk",
          numbersData,
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">uploadInventory()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      reject(getSessionExpiredError());
    }
  });
}

// Upload devices (Import devices)
export function uploadDevices(deviceData, cancelToken) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .post(
          AppConfigProps.serverRoutePrefix +
            "/subscribers/numbers/devices/bulk",
          deviceData,
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">uploadInventory()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      reject(getSessionExpiredError());
    }
  });
}

// Get Inventory Numbers Propagation Import information
export function getInventoryNumbersPropagation(extractType, cancelToken) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .get(
          AppConfigProps.serverRoutePrefix +
            `/subscribers/numbers/bulk?extractType=${extractType}`,
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">getInventoryNumbersPropagation()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      reject(getSessionExpiredError());
    }
  });
}

export function getInventoryDownloadFileData(
  countryList,
  intlNamespace,
  inventoryData
) {
  let headerColumns = [];
  headerColumns.push(
    IntlUtil.getText(intlNamespace, "content.countryName").replace(
      new RegExp(" ", "g"),
      ""
    ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.phoneNumber").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.userName").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.streetAddress1").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.streetAddress2").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.city").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.state").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.country").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.postalCode").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.carrier").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.type").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.department").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.managedOrunmanaged").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.classification").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.assignedOrunassigned").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.reservedOrunreserved").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.notes").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.updateStatus").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.errorMessage").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "\n"
  );
  let dataColumns = [];
  if (inventoryData && inventoryData.length > 0) {
    dataColumns = inventoryData.map((data) => {
      let number = removePhoneNumberDialCodebyCountryName(
        countryList,
        data.phoneNumber,
        data.numberCountryName
      );
      let countryName = "";
      if (
        data.numberCountryName != null &&
        data.numberCountryName.trim().length > 0
      )
        countryName = dataEditor(data.numberCountryName);
      let phoneNumber = "";
      if (number != null && number.trim().length > 0)
        phoneNumber = dataEditor(number);
      let userName = "";
      if (data.userName != null && data.userName.trim().length > 0)
        userName = dataEditor(data.userName);
      let streetAddress1 = "";
      if (data.streetAddress1 != null && data.streetAddress1.trim().length > 0)
        streetAddress1 = dataEditor(data.streetAddress1);
      let streetAddress2 = "";
      if (data.streetAddress2 != null && data.streetAddress2.trim().length > 0)
        streetAddress2 = dataEditor(data.streetAddress2);
      let city = "";
      if (data.city != null && data.city.trim().length > 0)
        city = dataEditor(data.city);
      let state = "";
      if (data.state != null && data.state.trim().length > 0)
        state = dataEditor(data.state);
      let country = "";
      if (data.country != null && data.country.trim().length > 0)
        country = dataEditor(data.country);
      let postalCode = "";
      if (data.postalCode != null && data.postalCode.trim().length > 0)
        postalCode = dataEditor(data.postalCode);
      let carrierName = "";
      if (data.carrierName != null && data.carrierName.trim().length > 0)
        carrierName = dataEditor(data.carrierName);
      let numberTypeName = "";
      if (data.numberTypeName != null && data.numberTypeName.trim().length > 0)
        numberTypeName = dataEditor(data.numberTypeName);
      let departmentName = "";
      if (data.departmentName != null && data.departmentName.trim().length > 0)
        departmentName = dataEditor(data.departmentName);
      let manageStatus = "";
      if (data.manageStatus != null && data.manageStatus.trim().length > 0)
        manageStatus = dataEditor(data.manageStatus);
      let classification = "";
      if (data.classification != null && data.classification.trim().length > 0)
        classification =
          data.classification?.toLowerCase().trim() ===
          VoiceAdminConstants.INVENTORY.CLASSIFICATION_TYPE.E_FAX?.toLowerCase().trim()
            ? VoiceAdminConstants.INVENTORY.CLASSIFICATION_TYPE.E_FAX
            : dataEditor(capitalizeFirstLetter(data.classification));
      let assignStatus = "";
      if (data.assignStatus != null && data.assignStatus.trim().length > 0)
        assignStatus = dataEditor(data.assignStatus);
      let reserveStatus = "";
      if (data.reserveStatus != null && data.reserveStatus.trim().length > 0)
        reserveStatus = dataEditor(data.reserveStatus);
      let numberNotes = "";
      if (data.numberNotes != null && data.numberNotes.trim().length > 0)
        numberNotes = dataEditor(data.numberNotes);
      let updateStatus = "";
      if (data.updateStatus) updateStatus = dataEditor(data.updateStatus);
      let errorMessage = "";
      if (data.errorMessage) errorMessage = dataEditor(data.errorMessage);

      return (
        countryName +
        "," +
        phoneNumber +
        "," +
        userName +
        "," +
        streetAddress1 +
        "," +
        streetAddress2 +
        "," +
        city +
        "," +
        state +
        "," +
        country +
        "," +
        postalCode +
        "," +
        carrierName +
        "," +
        numberTypeName +
        "," +
        departmentName +
        "," +
        manageStatus +
        "," +
        classification +
        "," +
        assignStatus +
        "," +
        reserveStatus +
        "," +
        numberNotes +
        "," +
        updateStatus +
        "," +
        errorMessage
      );
    });
  }

  return headerColumns.join("\n").concat(dataColumns.join("\n"));
}

export function downloadDeviceFileData(countryList, intlNamespace, deviceData) {
  let headerColumns = [];
  headerColumns.push(
    IntlUtil.getText(intlNamespace, "content.countryName").replace(
      new RegExp(" ", "g"),
      ""
    ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.phoneNumber").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.deviceType").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.makeBrand").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.model").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.serialNumber").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.updateStatus").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.errorMessage").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "\n"
  );
  let dataColumns = [];
  if (deviceData && deviceData.length > 0) {
    dataColumns = deviceData.map((data) => {
      let number = removePhoneNumberDialCodebyCountryName(
        countryList,
        data.phoneNumber,
        data.numberCountryName
      );
      let countryName = "";
      if (
        data.numberCountryName != null &&
        data.numberCountryName.trim().length > 0
      )
        countryName = dataEditor(data.numberCountryName);
      let phoneNumber = "";
      if (number != null && number.trim().length > 0)
        phoneNumber = dataEditor(number);
      let deviceTypeName = "";
      if (data.deviceTypeName != null && data.deviceTypeName.trim().length > 0)
        deviceTypeName = dataEditor(data.deviceTypeName);
      let makeName = "";
      if (data.makeName != null && data.makeName.trim().length > 0)
        makeName = dataEditor(data.makeName);
      let modelName = "";
      if (data.modelName != null && data.modelName.trim().length > 0)
        modelName = dataEditor(data.modelName);
      let serialNumber = "";
      if (data.serialNumber != null && data.serialNumber.trim().length > 0)
        serialNumber = dataEditor(data.serialNumber);
      let updateStatus = "";
      if (data.updateStatus) updateStatus = dataEditor(data.updateStatus);
      let errorMessage = "";
      if (data.errorMessage) errorMessage = dataEditor(data.errorMessage);

      return (
        countryName +
        "," +
        phoneNumber +
        "," +
        deviceTypeName +
        "," +
        makeName +
        "," +
        modelName +
        "," +
        serialNumber +
        "," +
        updateStatus +
        "," +
        errorMessage
      );
    });
  }

  return headerColumns.join("\n").concat(dataColumns.join("\n"));
}

export function getDeviceTypeList(cancelToken) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .get(
          AppConfigProps.serverRoutePrefix + `/subscribers/device-types`,
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">getDeviceTypeList()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      reject(getSessionExpiredError());
    }
  });
}

export function getDevicesByNumberId(numberId, cancelToken) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .get(
          AppConfigProps.serverRoutePrefix +
            `/subscribers/numbers/${numberId}/devices`,
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">getDevicesByNumberId()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      reject(getSessionExpiredError());
    }
  });
}

export function addDevicesByNumberId(numberId, deviceObj, cancelToken) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .post(
          AppConfigProps.serverRoutePrefix +
            `/subscribers/numbers/${numberId}/devices`,
          deviceObj,
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">addDevicesByNumberId()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      reject(getSessionExpiredError());
    }
  });
}

export function updateDevicesByNumberId(
  numberId,
  deviceId,
  deviceObj,
  cancelToken
) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .put(
          AppConfigProps.serverRoutePrefix +
            `/subscribers/numbers/${numberId}/devices/${deviceId}`,
          deviceObj,
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">addDevicesByNumberId()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      reject(getSessionExpiredError());
    }
  });
}

export function deleteDevice(numberId, deviceId, cancelToken) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .delete(
          AppConfigProps.serverRoutePrefix +
            `/subscribers/numbers/${numberId}/devices/${deviceId} `,
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">deleteDevice()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      reject(getSessionExpiredError());
    }
  });
}

export function getDeviceById(numberId, deviceId, cancelToken) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .get(
          AppConfigProps.serverRoutePrefix +
            `/subscribers/numbers/${numberId}/devices/${deviceId}`,
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">getDeviceById()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      reject(getSessionExpiredError());
    }
  });
}

export function getDevice(cancelToken) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .get(
          AppConfigProps.serverRoutePrefix + `/subscribers/numbers/devices`,
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">getDevice()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      reject(getSessionExpiredError());
    }
  });
}

export function getDeviceMakeModel(cancelToken) {
  return new Promise(function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      axios
        .get(
          AppConfigProps.serverRoutePrefix + `/subscribers/device-make-models`,
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">getDeviceMakeModel()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      reject(getSessionExpiredError());
    }
  });
}

export function setDeviceMakeModel(setNumberDeviceMakeModelData) {
  return async function (dispatch) {
    try {
      dispatch({
        type: INVENTORY_DEVICE_MAKE_MODEL_DATA,
        payload: setNumberDeviceMakeModelData,
      });
    } catch (err) {
      throw Error(err);
    }
  };
}

export function getTenantAccountsNumberSyncNow(cancelToken) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .get(
          AppConfigProps.serverRoutePrefix + `/tenant-accounts/numbers/sync`,
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">getTenantAccountsNumberSyncNow()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      reject(getSessionExpiredError());
    }
  });
}

export function tenentAccountNumberSyncNow(userData, cancelToken) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .post(
          AppConfigProps.serverRoutePrefix + "/tenant-accounts/numbers/sync",
          userData,
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">tenentAccountNumberSyncNow()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      reject(getSessionExpiredError());
    }
  });
}
export function getCommunicationPlatforms(cancelToken) {
  return new Promise(function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      axios
        .get(
          AppConfigProps.serverRoutePrefix + "/general/communication-platforms",
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">getCommunicationPlatforms()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      reject(getSessionExpiredError());
    }
  });
}
export function setCommunicationPlatforms(inventoryNumbersAllRecords) {
  return async function (dispatch) {
    try {
      dispatch({
        type: COMUNICATION_PLATFORMS_DATA,
        payload: inventoryNumbersAllRecords,
      });
    } catch (err) {
      throw Error(err);
    }
  };
}

export function getCommunicationPlatformByName(
  communicationPlatformData,
  communicationPlatform
) {
  if (
    communicationPlatform &&
    communicationPlatformData &&
    communicationPlatformData.length > 0
  ) {
    let platformObject = _.find(communicationPlatformData, (item) => {
      return (
        item.platformName?.toLowerCase().trim() ===
        communicationPlatform?.toLowerCase().trim()
      );
    });
    if (platformObject !== null || platformObject !== undefined) {
      return platformObject;
    } else {
      return null;
    }
  } else {
    return null;
  }
}

export function handleImageSource(src) {
  if (src && !src?.startsWith("https://")) {
    src = `data:image;base64,${src}`;
  }
  return src;
}

const getValue = (value) => {
  return value?.toLowerCase();
};

export function numberMultipleFilterCriteriaArray(array, filters) {
  const filterKeys = Object.keys(filters);
  return array.filter((number) => {
    return filterKeys.every((key) => {
      if (!filters[key].length) return true;
      return filters[key].some(
        (filter) => getValue(filter) === getValue(number[key])
      );
    });
  });
}
